import { useState, useEffect } from 'react';
import { validateEnvironment, testApiConnectivity, getSetupInstructions, type ConfigStatus } from '../lib/configValidator';
import './ConfigurationCheck.css';

interface ConfigurationCheckProps {
  onValidated: () => void;
}

export default function ConfigurationCheck({ onValidated }: ConfigurationCheckProps) {
  const [checking, setChecking] = useState(true);
  const [envValidation, setEnvValidation] = useState<ReturnType<typeof validateEnvironment> | null>(null);
  const [apiStatuses, setApiStatuses] = useState<ConfigStatus[]>([]);
  const [testingApis, setTestingApis] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Validate environment variables
    const validation = validateEnvironment();
    setEnvValidation(validation);

    if (validation.valid) {
      // Auto-proceed if all required keys are present
      onValidated();
      setChecking(false);
    } else {
      setChecking(false);
    }
  }, [onValidated]);

  const handleTestApis = async () => {
    setTestingApis(true);
    try {
      const statuses = await testApiConnectivity();
      setApiStatuses(statuses);
    } catch (error) {
      console.error('Failed to test APIs:', error);
    } finally {
      setTestingApis(false);
    }
  };

  const handleProceedAnyway = () => {
    if (confirm('Are you sure you want to proceed without proper configuration? Scans will likely fail.')) {
      onValidated();
    }
  };

  if (checking) {
    return (
      <div className="config-check">
        <div className="config-check-modal">
          <div className="checking-spinner">⚙️</div>
          <h2>Checking Configuration...</h2>
        </div>
      </div>
    );
  }

  if (!envValidation) return null;

  if (envValidation.valid) {
    return null; // Auto-proceeded
  }

  return (
    <div className="config-check">
      <div className="config-check-modal">
        <div className="modal-header">
          <h2>⚠️ Configuration Required</h2>
          <p>Some API keys are missing or invalid. Please configure them before proceeding.</p>
        </div>

        <div className="validation-results">
          {envValidation.errors.length > 0 && (
            <div className="errors-section">
              <h3>❌ Errors ({envValidation.errors.length})</h3>
              <ul>
                {envValidation.errors.map((error, idx) => (
                  <li key={idx} className="error-item">{error}</li>
                ))}
              </ul>
            </div>
          )}

          {envValidation.warnings.length > 0 && (
            <div className="warnings-section">
              <h3>⚠️ Warnings ({envValidation.warnings.length})</h3>
              <ul>
                {envValidation.warnings.map((warning, idx) => (
                  <li key={idx} className="warning-item">{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {apiStatuses.length > 0 && (
          <div className="api-test-results">
            <h3>🔌 API Connectivity Tests</h3>
            <div className="status-grid">
              {apiStatuses.map((status) => (
                <div key={status.service} className={`status-card ${status.tested ? 'success' : status.configured ? 'warning' : 'error'}`}>
                  <div className="status-header">
                    <span className="service-name">{status.service}</span>
                    <span className="status-indicator">
                      {!status.configured && '❌'}
                      {status.configured && !status.tested && '⚠️'}
                      {status.configured && status.tested && '✅'}
                    </span>
                  </div>
                  <div className="status-details">
                    {!status.configured && <span className="status-text">Not configured</span>}
                    {status.configured && !status.tested && <span className="status-text">Configured (not tested)</span>}
                    {status.configured && status.tested && <span className="status-text">Connected successfully</span>}
                  </div>
                  {status.error && (
                    <div className="status-error">{status.error}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="setup-instructions">
          <button
            className="btn-toggle-instructions"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? '▼' : '▶'} Setup Instructions
          </button>

          {showDetails && (
            <div className="instructions-content">
              <pre>{getSetupInstructions(envValidation.errors)}</pre>
            </div>
          )}
        </div>

        <div className="modal-actions">
          {apiStatuses.length === 0 && (
            <button
              className="btn-secondary"
              onClick={handleTestApis}
              disabled={testingApis}
            >
              {testingApis ? 'Testing APIs...' : 'Test API Connectivity'}
            </button>
          )}

          <button
            className="btn-warning"
            onClick={handleProceedAnyway}
          >
            Proceed Anyway (Not Recommended)
          </button>
        </div>

        <div className="help-text">
          <p>
            💡 <strong>Tip:</strong> Create a <code>.env</code> file in the project root with your API keys.
            See <code>.env.example</code> for the template.
          </p>
        </div>
      </div>
    </div>
  );
}
